import React, { useCallback, useEffect, useState } from 'react';
import './App.scss';
import { Worlds } from "./objects/Worlds/Worlds";
import { EmissionToggle } from "./components/EmissionToggle/EmissionToggle";
import StateOfTheClimateSvg  from "./assets/svg/state-of-the-climate.svg";
import { Footer } from "./components/Footer/Footer";
import { TouchBalloons } from "./components/TouchBalloons/TouchBalloons";
import { Popover } from "./components/Popover/Popover";
import { Button } from "./components/Button/Button";
import { DeveloperForm } from "./components/DeveloperForm/DeveloperForm";
import { YearInfo } from "./components/YearInfo/YearInfo";
import { ProgressCircle } from "./components/ProgressCircle/ProgressCircle";
import { EmissionChoice } from "./components/EmissionChoice/EmissionChoice";
import { useIdleTimer } from 'react-idle-timer'
import { Shadow } from "./components/Shadow/Shadow";
import { TouchFeedback } from "./components/TouchFeedback/TouchFeedback";

// export const runningOnHolobox = document.location.hash === '#holo';
export const runningOnHolobox = false;

export const sceneCount = 13;

export const ThreeDeeContext = React.createContext({
    sliderValue: 0,
    emission: false,
});

interface TouchState {
    timestamp: Date,
    appState: AppState,
    year: number;
    emissionState: string;
}

type AppState = 'loading' | 'splash' | 'intro' | 'first-use' | 'using';

function App() {
    const [modelProgress, setModelProgress] = useState(0);
    const [sliderValue, setSliderValue] = useState(0);
    const [sliderState, setSliderState] = useState(0);
    const [emissionState, setEmissionState] = useState(false);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [appState, setAppState] = useState<AppState>('loading');
    const [showDeveloperForm, setShowDeveloperForm] = useState(false);
    const [customProps, setCustomProps] = useState<{scene?: string, csvEmissionData?: string, csvZeroEmissionData?: string}>({ scene: undefined, csvEmissionData: undefined, csvZeroEmissionData: undefined });
    const [delayedYear, setDelayedYear] = useState(2020);
    const year = 2020 + (sliderState * 5);

    const resetApp = () => {
        setSliderValue(0);
        setSliderState(0);
        setEmissionState(false);
        setAppState('splash');
    }

    const idleTimeout = useIdleTimer({
        onIdle: () => {
            if (['using', 'intro', 'first-use'].includes(appState)) {
                resetApp();
            }
        },
        timeout: 40 * 1000,
        startManually: true,
    });
    const startIdleTimeout = idleTimeout.start;

    useEffect(() => {
        const p = Math.round(sceneCount * (sliderValue / 100));
        setSliderState(p);
    }, [sliderValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDelayedYear(year);
        }, 500);
        return () => {
            clearTimeout(timeout);
        }
    }, [year]);

    useEffect(() => {
        if (modelLoaded) {
            if (window.location.host.split('localhost').length === 1) {
                startIdleTimeout();
            }
            setAppState('splash');
        }
    }, [modelLoaded, startIdleTimeout]);

    useEffect(() => {
        const handleKeydown = ({ key, ctrlKey }: any) => {
            if (ctrlKey && key === 'u') {
                setShowDeveloperForm(true);
            }
        }
        window.addEventListener('keydown', handleKeydown);

        return () => {
            window.removeEventListener('keydown', handleKeydown);
        }
    }, []);

    const handleDeveloperForm = (scene?: string, csvEmissionData?: string, csvZeroEmissionData?: string) => {
        setCustomProps({
            scene,
            csvEmissionData,
            csvZeroEmissionData,
        })
        setShowDeveloperForm(false);
    }

    const changeSliderValue = (action: 'increase' | 'decrease') => {
        const oneStep = 100 / sceneCount;
        setSliderValue((prevState) => {
            const newProgressUp = Math.round(sceneCount * ((prevState + oneStep) / 100));
            const newProgressDown = Math.round(sceneCount * ((prevState - oneStep) / 100));
            if (action === 'increase' && newProgressUp <= sceneCount) {
                return prevState + oneStep;
            } else if (action === 'decrease' && newProgressDown >= 0) {
                return prevState - oneStep;
            }
            return prevState;
        })
    };

    const getStoredTouches = (): TouchState[] => {
        const touches = window.localStorage.getItem('touches');
        if (touches) {
            return JSON.parse(touches).map((touch: any) => {
                touch.timestamp = new Date(touch.timestamp);
                return touch;
            });
        }
        return [];
    }
    const saveTouch = useCallback(() => {
        const date = new Date();
        const touches = getStoredTouches();
        touches.push({
            timestamp: date,
            appState,
            year,
            emissionState: emissionState ? 'As it is' : 'Improved'
        });
        window.localStorage.setItem('touches', JSON.stringify(touches));
    }, [appState, year, emissionState]);

    const handleTouchTrack = () => {
        saveTouch();
    }

    const exportTouches = () => {
        const lines: string[] = [
            'Date;Time;App state;Emission;Year'
        ];

        const touches = getStoredTouches();
        touches.forEach((touch) => {
            const leadingZero = (char: number) => {
                const strChar = char.toString();
                if (strChar.length === 1) {
                    return `0${strChar}`;
                }
                return strChar;
            };
            const line = [
                leadingZero(touch.timestamp.getDate()) + '-' + leadingZero(touch.timestamp.getMonth() + 1) + '-' + leadingZero(touch.timestamp.getFullYear()),
                leadingZero(touch.timestamp.getHours()) + ':' + leadingZero(touch.timestamp.getMinutes()),
                touch.appState,
                touch.emissionState,
                touch.year
            ];
            lines.push(line.map(v => `${v}`).join(';'));
        })

        const csvString = lines
            .join("\r\n");

        let csvContent = `data:text/csv;charset=utf-8,${csvString}`;

        const encodedUri = encodeURI(csvContent);

        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `touch-log.csv`);

        // eslint-disable-next-line no-restricted-globals
        const purgeLocalStorage = confirm('Remove current set after downloading?');
        if (purgeLocalStorage) {
            window.localStorage.removeItem('touches');
        }

        document.body.appendChild(link); // Required for FF

        link.click();
    }

    useEffect(() => {
        const bodyClassList = (document.querySelector('body') as any as HTMLBodyElement).classList;
        if (runningOnHolobox) {
            bodyClassList.add('running-on-holobox');
        } else {
            bodyClassList.remove('running-on-holobox');
        }
    }, []);

    return (
        <ThreeDeeContext.Provider value={{
            sliderValue: sliderState,
            emission: emissionState
        }}>
            <div className={`App state-${appState}`}>
                <div className="hidden-reset-button" onClick={resetApp} />
                <TouchFeedback onTouch={handleTouchTrack} />
                <div className="splash-text">
                    <img src={StateOfTheClimateSvg} alt="State of the Climate"/>
                </div>
                <div className="progress-wrapper">
                    <ProgressCircle progress={modelProgress} />
                </div>
                <div className="dark-loading-overlay">
                    <div className="noise" />
                </div>
                <TouchBalloons onClick={() => {
                    setAppState('intro');
                }} show={appState === 'splash'}/>

                <Popover title={'Introduction'} show={appState === 'intro'}>
                    <p>We all know that the climate is not doing well. The earth’s temperature is getting higher and higher. One of the ways we can solve climate change is by reducing CO2 emissions. In the holobox you’re in front of right now, we’ve visualized data from the World Bank’s Climate Change Knowledge Portal. With this we can show what the consequences will be for the Netherlands if we do not reduce CO2 emissions in the coming decades. Play with the data and see for yourself that it is time for change!</p>
                    <div className="popover-button-wrapper">
                        <Button variant={'yellow'} onClick={() => {
                            setAppState('first-use')
                        }}>Start here</Button>
                    </div>
                </Popover>

                <EmissionChoice setEmissionState={(value) => {
                    setEmissionState(value);
                    setAppState('using');
                }} visible={appState === 'first-use'} />


                <div className="cube-wrapper">
                    <Shadow position={'wall'} />
                    <Shadow position={'floor'} />
                    <Shadow position={'floor-faded'} />
                    <Worlds setModelLoaded={setModelLoaded} customProps={customProps} setProgress={setModelProgress}/>
                </div>

                <YearInfo visible={appState === 'using'} year={delayedYear} emission={emissionState} changeSliderValue={changeSliderValue} setSliderValue={setSliderValue}
                sliderValue={sliderValue}>
                    <EmissionToggle value={emissionState} onChange={setEmissionState} />
                </YearInfo>

                <Footer onTouchExport={exportTouches} />

                <div id="debug" />

                {showDeveloperForm ? <DeveloperForm setStuff={handleDeveloperForm} /> : null}
            </div>
        </ThreeDeeContext.Provider>
    );
}

export default App;
